function canonicalSegment(value, label) {
    const normalized = value.trim();
    if (!normalized)
        throw new Error(`${label} cannot be empty`);
    if (normalized.includes(':'))
        throw new Error(`${label} cannot contain ':'`);
    return normalized;
}
/** Jira-like issue key pattern (e.g. DEV-3056, ABC-1). */
const JIRA_TICKET_KEY_RE = /\b([A-Z][A-Z0-9]+-\d+)\b/g;
/**
 * Extract unique Jira-style ticket keys from free text (PR title, branch, body).
 * Order preserved; first match is typically used as `linkedTicketKey`.
 */
export function extractJiraTicketKeys(...texts) {
    const seen = new Set();
    const keys = [];
    for (const text of texts) {
        if (!text || typeof text !== 'string')
            continue;
        const re = new RegExp(JIRA_TICKET_KEY_RE.source, 'g');
        for (const match of text.matchAll(re)) {
            const key = match[1];
            if (!seen.has(key)) {
                seen.add(key);
                keys.push(key);
            }
        }
    }
    return keys;
}
const GITHUB_PR_URL_RE = /https?:\/\/github\.com\/([^/\s]+)\/([^/\s]+)\/pull\/(\d+)/gi;
const GITHUB_PR_SHORTHAND_RE = /\b([A-Za-z0-9_.-]+)\/([A-Za-z0-9_.-]+)#(\d+)\b/g;
function mentionKey(mention) {
    return `${mention.owner.toLowerCase()}/${mention.repo.toLowerCase()}#${mention.number}`;
}
function pushMention(mentions, seen, owner, repo, number) {
    const mention = { owner, repo, number };
    const key = mentionKey(mention);
    if (seen.has(key))
        return;
    seen.add(key);
    mentions.push(mention);
}
/** Extract unique GitHub pull mentions from free text (URLs or owner/repo#N). */
export function extractGitHubPullRequestMentions(...texts) {
    const seen = new Set();
    const mentions = [];
    for (const text of texts) {
        if (!text || typeof text !== 'string')
            continue;
        const urlRe = new RegExp(GITHUB_PR_URL_RE.source, 'gi');
        for (const match of text.matchAll(urlRe)) {
            pushMention(mentions, seen, match[1], match[2], Number(match[3]));
        }
        const shortRe = new RegExp(GITHUB_PR_SHORTHAND_RE.source, 'g');
        for (const match of text.matchAll(shortRe)) {
            pushMention(mentions, seen, match[1], match[2], Number(match[3]));
        }
    }
    return mentions;
}
/** Stable PR id used by the live GitHub BFF and HttpGitHubClient (no `/` for path safety). */
export function githubPullRequestId(owner, repo, number) {
    return `${canonicalSegment(owner, 'owner')}~${canonicalSegment(repo, 'repo')}~${number}`;
}
/** Canonical browse URL for a pull request on github.com. */
export function githubPullRequestHtmlUrl(owner, repo, number) {
    return `https://github.com/${canonicalSegment(owner, 'owner')}/${canonicalSegment(repo, 'repo')}/pull/${number}`;
}
/** Owner/repo segments are interpolated into GitHub API paths: never allow `.`/`..`. */
function isSafeRepoSegment(segment) {
    return !/^\.+$/.test(segment);
}
/** Parse `owner~repo~number` (also tolerates legacy `owner/repo#number`, `PR-N`, `#N`). */
export function parseGitHubPullRequestId(raw) {
    const trimmed = raw.trim();
    const safe = /^([A-Za-z0-9._-]+)~([A-Za-z0-9._-]+)~(\d+)$/.exec(trimmed);
    if (safe) {
        return isSafeRepoSegment(safe[1]) && isSafeRepoSegment(safe[2])
            ? { owner: safe[1], repo: safe[2], number: Number(safe[3]) }
            : null;
    }
    const legacy = /^([A-Za-z0-9._-]+)\/([A-Za-z0-9._-]+)#(\d+)$/.exec(trimmed);
    if (legacy) {
        return isSafeRepoSegment(legacy[1]) && isSafeRepoSegment(legacy[2])
            ? { owner: legacy[1], repo: legacy[2], number: Number(legacy[3]) }
            : null;
    }
    const numbered = /^(?:PR-|#)?(\d+)$/i.exec(trimmed);
    if (numbered)
        return { number: Number(numbered[1]) };
    return null;
}
// ---------------------------------------------------------------------------
// Pure Slack mrkdwn protocol helpers (shared between API, BFF and Web)
// ---------------------------------------------------------------------------
const USER_MENTION_RE = /<@([UW][A-Z0-9]+)(?:\|([^>]+))?>/g;
const CHANNEL_MENTION_RE = /<#([CG][A-Z0-9]+)(?:\|([^>]+))?>/g;
const LABELED_LINK_RE = /<(https?:\/\/[^|>]+)\|([^>]+)>/g;
const BARE_LINK_RE = /<(https?:\/\/[^>]+)>/g;
const SPECIAL_CMD_RE = /<!(?:here|channel|everyone)(?:\|[^>]*)?>/g;
const SUBTEAM_RE = /<!subteam\^[^|>]+(?:\|([^>]+))?>/g;
/** Collect <@U…> user IDs from raw Slack mrkdwn text. */
export function extractMentionedUserIds(text) {
    if (!text || typeof text !== 'string')
        return [];
    const ids = [];
    const re = new RegExp(USER_MENTION_RE.source, 'g');
    for (const match of text.matchAll(re)) {
        ids.push(match[1]);
    }
    return ids;
}
/** Strip Slack emphasis (*, _, ~, ```, `) while preserving inner text. */
export function stripSlackEmphasis(text) {
    let out = text;
    out = out.replace(/```(?:[\w+-]*)?\n?([\s\S]*?)```/g, '$1');
    out = out.replace(/`([^`]+)`/g, '$1');
    out = out.replace(/\*([^*\n]+)\*/g, '$1');
    out = out.replace(/_([^_\n]+)_/g, '$1');
    out = out.replace(/~([^~\n]+)~/g, '$1');
    return out;
}
/** Resolve Slack entities (<@U>, <#C|name>, <url|label>) into clean plain text. */
export function formatSlackMrkdwn(text, names = {}) {
    if (!text)
        return '';
    let out = text;
    out = out.replace(USER_MENTION_RE, (_m, id, label) => {
        const resolved = (label && label.trim()) || names[id] || id;
        return `@${resolved}`;
    });
    out = out.replace(CHANNEL_MENTION_RE, (_m, _id, label) => {
        const name = (label && label.trim()) || 'channel';
        return name.startsWith('#') ? name : `#${name}`;
    });
    out = out.replace(LABELED_LINK_RE, (_m, _url, label) => label);
    out = out.replace(BARE_LINK_RE, (_m, url) => url);
    out = out.replace(SPECIAL_CMD_RE, (m) => {
        if (m.includes('here'))
            return '@here';
        if (m.includes('channel'))
            return '@channel';
        return '@everyone';
    });
    out = out.replace(SUBTEAM_RE, (_m, label) => (label ? `@${label}` : '@group'));
    out = stripSlackEmphasis(out);
    return out
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/[ \t]{2,}/g, ' ')
        .trim();
}
//# sourceMappingURL=crossPlatform.js.map